import { Button } from '@mui/material';
import { CloudDownload } from '@mui/icons-material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { EventParticipantStatusEnum } from 'src/shared/models/enums/eventParticipantStatus.enum';
import { getFormSettings, EventFormSetting } from 'src/shared/services/eventFormSettingsService';
import { EventParticipant } from '../models/eventParticipant';

type EventDetailExportParticipantButtonProps = {
  participants: EventParticipant[];
  eventId: number;
};

export const EventDetailExportParticipantButton = (props: EventDetailExportParticipantButtonProps) => {
  const { participants, eventId } = props;

  const { t } = useTranslation(['event', 'invoice']);
  const [formSettings, setFormSettings] = useState<EventFormSetting[]>([]);

  const fetchFormSettings = useCallback(async () => {
    try {
      const res = await getFormSettings(eventId);
      setFormSettings(res.data);
    } catch {
      // Ignorera — faller tillbaka på default-namn
    }
  }, [eventId]);

  useEffect(() => {
    fetchFormSettings();
  }, [fetchFormSettings]);

  const getCustomHeader = (fieldName: string) => {
    const setting = formSettings.find((s) => s.fieldName === fieldName);
    return setting?.heading || fieldName;
  };

  const handleExport = () => {
    // Bygg custom-kolumner — inkludera bara de som har konfigurerade fält
    const customColumns = Array.from({ length: 10 }, (_, i) => {
      const fieldName = `custom${i + 1}`;
      return { field: fieldName, header: getCustomHeader(fieldName) };
    }).filter((col) => formSettings.some((s) => s.fieldName === col.field));

    const exportColumns = [
      { field: 'memberNr', header: t('event:tableHeaders.memberNr') },
      { field: 'firstName', header: t('event:tableHeaders.firstName') },
      { field: 'lastName', header: t('event:tableHeaders.lastName') },
      { field: 'email', header: t('event:tableHeaders.email') },
      { field: 'phone', header: t('event:tableHeaders.phone') },
      { field: 'allergy', header: t('event:tableHeaders.allergies') },
      { field: 'status', header: t('event:tableHeaders.status') },
      { field: 'date', header: t('event:tableHeaders.date') },
      { field: 'invoice', header: t('event:tableHeaders.invoice') },
      { field: 'amount', header: t('event:tableHeaders.amount') },
      { field: 'paymentStatus', header: t('event:tableHeaders.status') },
      ...customColumns,
    ];

    const getParticipantStatusLabel = (status?: EventParticipantStatusEnum) => {
      if (status === EventParticipantStatusEnum.Unanswered) return t('event:participantStatuses.unanswered');
      if (status === EventParticipantStatusEnum.Attending) return t('event:participantStatuses.attending');
      if (status === EventParticipantStatusEnum.NotAttending) return t('event:participantStatuses.notAttending');
      if (status === EventParticipantStatusEnum.Cancelled) return t('event:participantStatuses.cancelled');
      return '';
    };

    const getInvoiceStatusLabel = (status?: string) => {
      const invoiceStatusTranslations = {
        Betald: 'Paid',
        Obetald: 'UnPaid',
        Delbetald: 'PartlyPaid',
        Förfallen: 'Overdue',
        Makulerad: 'Canceled',
      } as const;

      if (!status) return '';

      const translationKey = invoiceStatusTranslations[status as keyof typeof invoiceStatusTranslations];
      return translationKey ? t(`invoice:invoiceStatus.${translationKey}`) : status;
    };

    const getExportValue = (participant: EventParticipant, field: (typeof exportColumns)[number]['field']) => {
      switch (field) {
        case 'status':
          return getParticipantStatusLabel(participant.status);
        case 'date':
          return DateHelper.getDateTime(participant.createdAt) || '';
        case 'invoice':
          return participant.invoiceOcr || '';
        case 'amount':
          return participant.invoiceAmount ?? '';
        case 'paymentStatus':
          return getInvoiceStatusLabel(participant.invoiceStatus);
        default:
          return participant[field] ?? '';
      }
    };

    const items = participants;
    const replacer = (key, value) => (value === null ? '' : value);
    const header = exportColumns.map((col) => col.header);
    const csv = [
      header.join(','),
      ...items.map((row) => exportColumns.map((column) => JSON.stringify(getExportValue(row, column.field), replacer)).join(',')),
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `eventdeltagare_(${items.length})_${dayjs(new Date()).format('YYYYMMDD_HHmm')}.csv`;
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(<>{t('invoice:export.exportSuccess')}</>);
    }
  };

  return (
    <Button
      size='small'
      variant='outlined'
      endIcon={<CloudDownload fontSize='small' />}
      onClick={() => {
        handleExport();
      }}
    >
      {t('common:button.export')}
    </Button>
  );
};
